import { Stack, StackProps } from 'component-library';
import React from 'react';

import { IndicatorFactType } from '@/api/rest/resources/types/fact';
import { BenchmarkExplainerType, GraphTileType, NCExplainerType, WidgetType } from '@/api/rest/resources/types/widget';
import { GraphTypeValue } from '@/components/Charts/hooks/useChartType';
import {
  NCCard,
  NCCard2SectionLayout,
  NCCardAnalysisType,
  NCCardAnalysisTypeToggle,
  NCCardComplianceFooter,
  NCCardHeader,
  useNCCardContext,
} from '@/components/NCCard/NCCard';
import { AboveGroundCarbonExplainer } from '@/components/NCExplainers/AboveGroundCarbonExplainer';
import { CarbonExplainer } from '@/components/NCExplainers/CarbonExplainer';
import { CarbonUpliftPotentialExplainer } from '@/components/NCExplainers/CarbonUpliftPotentialExplainer';
import { WaterExplainer } from '@/components/NCExplainers/WaterExplainer';
import { WhcUpliftPotentialExplainer } from '@/components/NCExplainers/WhcUpliftPotentialExplainer';
import { ComplianceFrameworkLogo } from '@/config/constants';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useWidgetByNameType } from '@/hooks/useWidgetByNameType';
import { AboveGroundCarbonCardGraphTile } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/aboveGroundCarbon/components/nc-breakdown/GraphTile';
import { BiodiversityCardGraphTile } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/biodiversity/components/biodiversity/GraphTile';
import { DeforestationGraphTile } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/deforestation/components/deforestation/GraphTile';
import { EcosystemClassificationGraphTile } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/ecosystemClassification/components/ecosystemClassification/GraphTile';
import { CanopyCoverGraphTile } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/forestStructure/components/canopyCover/GraphTile';
import { CanopyHeightGraphTile } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/forestStructure/components/canopyHeight/GraphTile';
import { HabitatConnectivityGraphTile } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/habitatConnnectivity/components/habitatConnectivity/GraphTile';
import { HabitatIntactnessGraphTile } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/HabitatIntactness/components/HabitatIntactness/GraphTile';
import { HabitatIntactnessThreatsBreakdownGraphTile } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/HabitatIntactness/components/ThreatsBreakdown/GraphTile';
import { CarbonGraphTile } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/impact-projections/components/CarbonGraphTile';
import { WaterGraphTile } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/impact-projections/components/WaterGraphTile';
import { LandcoverLanduseGraphTile } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/landcoverLanduse/components/GraphTile';
import { NDVIGraphTile } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/ndvi/components/ndvi/GraphTile';
import { ProtectedAreasGraphTile } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/protectedAreas/components/GraphTile';
import { SocialLifeExpectancyGraphTile } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/social/components/lifeExpectancy/GraphTile';
import { WaterCardGraphTile } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/water/components/nc-breakdown/GraphTile';
import { SMPrecipitationCardGraphTile } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/water/components/nc-breakdown/SMPrecipitationGraphTile';
import { isNullish } from '@/utils/booleans';

import { CarbonCardGraphTile } from '../../pages/shared/projects/pages/project/pages/sustainableProduction/pages/carbon/components/nc-breakdown/GraphTile';
import { ChartZoomStateHook } from '../Charts/components/ChartZoom';
import { BiodiversityExplainer } from '../NCExplainers/BiodiversityExplainer';
import { CanopyCoverExplainer } from '../NCExplainers/CanopyCoverExplainer';
import { CanopyHeightExplainer } from '../NCExplainers/CanopyHeightExplainer';
import { GradientContentProps, GradientScaleTile } from './GradientScaleTile';
import { GraphTile, GraphTileProps } from './GraphTile';
import { IndicatorProvider } from './IndicatorContextProvider';
import { StatTile, StatTileHeaderProps } from './StatsTile';

export type WidgetIndicatorCardProps = {
  name: string;
  type: WidgetType;
};

export const WidgetIndicatorCard = ({ name, type }: WidgetIndicatorCardProps) => {
  const widget = useWidgetByNameType(name, type);
  const props = widget?.widget_format ?? {};
  return <IndicatorCard key={`${name}-${type}`} {...props} />;
};

export type IndicatorCardHeaderProps = {
  icon?: React.ReactNode;
  title?: React.ReactNode;
  sectionHeader?: string;
  explainerType?: NCExplainerType;
  showToggle?: boolean;
  showZoom?: boolean;
  showCustomBenchmark?: boolean; // Extra flag for custom graphs. Otherwise, passing the graphType to GRAPHS_WITH_CUSTOM_BENCHMARKS is enough
  showGraphType?: boolean;
  headerTooltip?: React.ReactNode;
};

export type IndicatorCardFooterProps = {
  logos?: string[];
};

type DefinitionProps = {
  title?: string;
  body?: string;
};

export type IndicatorCardProps = {
  headerProps?: IndicatorCardHeaderProps;
  footerProps?: IndicatorCardFooterProps;
  graphTileType?: GraphTileType;
  graphTileProps?: GraphTileProps;
  statTileProps?: ProjectFactStatTileProps;
  definitionProps?: DefinitionProps;
  styles?: {
    header?: string;
    graphTile?: string;
    statTile?: string;
    footer?: string;
    root?: string;
  };
};

export const IndicatorCard = ({
  headerProps,
  graphTileType,
  graphTileProps,
  statTileProps,
  footerProps,
  styles,
}: IndicatorCardProps) => {
  const isSmallScreen = useScreenSize() === 'small';

  const showDivider = !isNullish(graphTileType) && !isNullish(statTileProps);

  return (
    <IndicatorProvider>
      <NCCard
        explainer={headerProps?.explainerType && GetExplainerByType(headerProps?.explainerType)}
        className={styles?.root}
      >
        {headerProps && (
          <NCCardHeader title={headerProps?.title} className={styles?.header}>
            {headerProps?.showToggle && !isSmallScreen && (
              <NCCardAnalysisTypeToggle data-testid='analysis-type-toggle' />
            )}
          </NCCardHeader>
        )}
        <NCCard2SectionLayout showDivider={showDivider}>
          <Stack className='items-center pt-4 sm:items-stretch sm:pt-0' center>
            {headerProps?.showToggle && isSmallScreen && (
              <NCCardAnalysisTypeToggle data-testid='analysis-type-toggle' />
            )}
            {graphTileType && GetGraphTileByType(graphTileType, graphTileProps ?? null)}
          </Stack>
          <Stack spacing={10} className='p-6' center>
            {statTileProps && (
              <ProjectFactStatTile
                contentProps={statTileProps.contentProps}
                headerProps={statTileProps.headerProps}
                className={styles?.statTile}
              />
            )}
            {footerProps?.logos && (
              <NCCardComplianceFooter
                logos={footerProps.logos as ComplianceFrameworkLogo[]}
                className={styles?.footer}
              />
            )}
          </Stack>
        </NCCard2SectionLayout>
      </NCCard>
    </IndicatorProvider>
  );
};

export const GetGraphTileByType = (
  type: GraphTileType,
  props: GraphTileProps | null,
  zoom?: ChartZoomStateHook['zoom'],
  setZoom?: ChartZoomStateHook['setZoom'],
  graphType?: GraphTypeValue,
  selectedBenchmark?: string | null,
) => {
  switch (type) {
    case GraphTileType.historic_below_ground_carbon:
      return (
        <CarbonCardGraphTile
          {...(props as GraphTileProps)}
          zoom={zoom}
          setZoom={setZoom}
          graphType={graphType}
          selectedBenchmark={selectedBenchmark}
        />
      );
    case GraphTileType.projection_below_ground_carbon:
      return <CarbonGraphTile {...(props as GraphTileProps)} />;
    case GraphTileType.historic_above_ground_carbon:
      return (
        <AboveGroundCarbonCardGraphTile
          {...(props as GraphTileProps)}
          zoom={zoom}
          setZoom={setZoom}
          graphType={graphType}
          selectedBenchmark={selectedBenchmark}
        />
      );
    case GraphTileType.historic_water_soil_moisture:
      return (
        <WaterCardGraphTile
          {...(props as GraphTileProps)}
          zoom={zoom}
          setZoom={setZoom}
          graphType={graphType}
          selectedBenchmark={selectedBenchmark}
        />
      );
    case GraphTileType.historic_precipitation_soil_moisture:
      return <SMPrecipitationCardGraphTile {...(props as GraphTileProps)} />;
    case GraphTileType.projection_water_soil_moisture:
      return <WaterGraphTile {...(props as GraphTileProps)} />;
    case GraphTileType.protected_on_farm_habitat:
      return (
        <BiodiversityCardGraphTile
          {...(props as GraphTileProps)}
          zoom={zoom}
          setZoom={setZoom}
          graphType={graphType}
          selectedBenchmark={selectedBenchmark}
        />
      );
    case GraphTileType.canopy_cover:
      return (
        <CanopyCoverGraphTile
          {...(props as GraphTileProps)}
          zoom={zoom}
          setZoom={setZoom}
          graphType={graphType}
          selectedBenchmark={selectedBenchmark}
        />
      );
    case GraphTileType.canopy_height:
      return (
        <CanopyHeightGraphTile
          {...(props as GraphTileProps)}
          graphType={graphType}
          selectedBenchmark={selectedBenchmark}
        />
      );
    case GraphTileType.social_life_expectancy:
      return (
        <SocialLifeExpectancyGraphTile
          {...(props as GraphTileProps)}
          graphType={graphType}
          zoom={zoom}
          setZoom={setZoom}
        />
      );
    case GraphTileType.ecosystem_classification:
      return <EcosystemClassificationGraphTile {...(props as GraphTileProps)} />;
    case GraphTileType.ndvi:
      return (
        <NDVIGraphTile
          {...(props as GraphTileProps)}
          zoom={zoom}
          setZoom={setZoom}
          graphType={graphType}
          selectedBenchmark={selectedBenchmark}
        />
      );
    case GraphTileType.historic_habitat_intactness:
      return (
        <HabitatIntactnessGraphTile
          {...(props as GraphTileProps)}
          graphType={graphType}
          zoom={zoom}
          setZoom={setZoom}
        />
      );
    case GraphTileType.historic_habitat_intactness_threats_breakdown:
      return (
        <HabitatIntactnessThreatsBreakdownGraphTile
          {...(props as GraphTileProps)}
          graphType={graphType}
          zoom={zoom}
          setZoom={setZoom}
        />
      );
    case GraphTileType.historic_deforestation:
      return (
        <DeforestationGraphTile
          {...(props as GraphTileProps)}
          graphType={graphType}
          selectedBenchmark={selectedBenchmark}
        />
      );
    case GraphTileType.habitat_connectivity:
      return <HabitatConnectivityGraphTile {...(props as GraphTileProps)} />;
    case GraphTileType.protected_areas:
      return <ProtectedAreasGraphTile selectedBenchmark={selectedBenchmark} {...(props as GraphTileProps)} />;
    case GraphTileType.landcover_landuse:
      return <LandcoverLanduseGraphTile selectedBenchmark={selectedBenchmark} {...(props as GraphTileProps)} />;
    case GraphTileType.custom:
      if (!props) return null;
      return <GraphTile selectedBenchmark={selectedBenchmark} {...props} />;
    case GraphTileType.gradient_scale:
      return <GradientScaleTile {...(props as GradientContentProps)} />;
    default:
      return <></>;
  }
};

const GetExplainerByType = (type: NCExplainerType) => {
  switch (type) {
    case NCExplainerType.historic_below_ground_carbon:
      return <CarbonExplainer />;
    case NCExplainerType.projection_below_ground_carbon:
      return <CarbonUpliftPotentialExplainer />;
    case NCExplainerType.historic_above_ground_carbon:
      return <AboveGroundCarbonExplainer />;
    case NCExplainerType.historic_water_soil_moisture:
      return <WaterExplainer />;
    case NCExplainerType.projection_water_soil_moisture:
      return <WhcUpliftPotentialExplainer />;
    case NCExplainerType.protected_on_farm_habitat:
      return <BiodiversityExplainer />;
    case NCExplainerType.canopy_cover:
      return <CanopyCoverExplainer />;
    case NCExplainerType.canopy_height:
      return <CanopyHeightExplainer />;
    default:
      return <></>;
  }
};

type StatFact = Record<NCCardAnalysisType, IndicatorFactType>;

const getAnalysisTypeFactType = (statFact?: StatFact, type?: NCCardAnalysisType) => {
  if (isNullish(statFact) || isNullish(type)) return null;

  return statFact[`${type}`];
};

type ProjectFactStatTileContentProps = {
  latestAnalysisStatFact?: StatFact;
  benchmarkGraphStatFact?: StatFact;
  oneYearChangeStatFact?: StatFact;
  oneYearTrendStatFact?: StatFact;
  benchmarkExplainerType?: BenchmarkExplainerType;
  showProjectionsLink?: boolean;
};

type ProjectFactStatTileProps = {
  headerProps?: StatTileHeaderProps;
  contentProps?: ProjectFactStatTileContentProps;
} & StackProps;

export const ProjectFactStatTile = ({ headerProps, contentProps, ...delegated }: ProjectFactStatTileProps) => {
  const { analysisType } = useNCCardContext();

  let statTileContentProps;
  if (contentProps) {
    const latestAnalysisFactType = getAnalysisTypeFactType(contentProps.latestAnalysisStatFact, analysisType);
    const benchmarkFactType = getAnalysisTypeFactType(contentProps.benchmarkGraphStatFact, analysisType);
    const oneYearTrendFactType = getAnalysisTypeFactType(contentProps.oneYearTrendStatFact, analysisType);
    const oneYearChangeFactType = getAnalysisTypeFactType(contentProps.oneYearChangeStatFact, analysisType);

    statTileContentProps = {
      latestAnalysisFactType,
      benchmarkFactType,
      oneYearTrendFactType,
      oneYearChangeFactType,
      benchmarkExplainerType: contentProps.benchmarkExplainerType,
      showProjectionsLink: contentProps.showProjectionsLink,
    };
  }

  return <StatTile headerProps={headerProps} contentProps={statTileContentProps} {...delegated} />;
};
